import { useParams } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import { 
  ChartBarIcon,
  UserGroupIcon,
  BuildingOffice2Icon,
  CheckCircleIcon
} from '@heroicons/react/24/outline'

const ServicesPage = () => {
  const { service } = useParams<{ service: string }>()

  const serviceData = {
    'market-entry': {
      title: 'International Market Entry',
      icon: ChartBarIcon,
      description: 'Strategic guidance and comprehensive support for entering new international markets with confidence and reduced risk.',
      features: [
        'Market Feasibility Analysis',
        'Regulatory Compliance Assessment',
        'Local Partnership Identification',
        'Entry Strategy Development',
        'Risk Assessment & Mitigation',
        'Cultural Adaptation Consulting'
      ],
      benefits: [
        'Reduced market entry risk',
        'Accelerated international expansion',
        'Local market expertise access',
        'Regulatory compliance assurance'
      ]
    },
    'business-development': {
      title: 'Business Development',
      icon: ChartBarIcon,
      description: 'Comprehensive growth strategies and partnership facilitation to expand your business presence in target markets.',
      features: [
        'Market Analysis & Research',
        'Partnership Development',
        'Growth Strategy Planning',
        'Competitive Intelligence',
        'Revenue Optimization',
        'Business Model Innovation'
      ],
      benefits: [
        'Accelerated market entry',
        'Strategic partnership networks',
        'Sustainable growth frameworks',
        'Risk mitigation strategies'
      ]
    },
    'consulting': {
      title: 'Strategic Consulting',
      icon: UserGroupIcon,
      description: 'Expert advisory services for complex international business decisions and regulatory compliance.',
      features: [
        'Strategic Planning',
        'Regulatory Compliance',
        'Risk Assessment',
        'Process Optimization',
        'Change Management',
        'Performance Analytics'
      ],
      benefits: [
        'Informed decision making',
        'Regulatory compliance assurance',
        'Operational efficiency',
        'Strategic clarity and direction'
      ]
    },
    'partnerships': {
      title: 'Corporate Partnerships',
      icon: BuildingOffice2Icon,
      description: 'Facilitating strategic alliances and joint ventures between international business partners.',
      features: [
        'Partner Identification',
        'Due Diligence',
        'Negotiation Support',
        'Contract Structuring',
        'Relationship Management',
        'Performance Monitoring'
      ],
      benefits: [
        'Access to new markets',
        'Shared resources and expertise',
        'Risk distribution',
        'Enhanced competitive advantage'
      ]
    }
  }

  const currentService = service && serviceData[service as keyof typeof serviceData]

  if (!currentService) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Service Not Found</h1>
          <p className="text-gray-600">The requested service could not be found.</p>
        </div>
      </div>
    )
  }

  const IconComponent = currentService.icon

  return (
    <>
      <Helmet>
        <title>{currentService.title} - Nexus Global Solutions</title>
        <meta name="description" content={currentService.description} />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 py-24">
          <div className="absolute inset-0 bg-black/20"></div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center"
            >
              <div className="flex justify-center mb-6">
                <div className="p-4 bg-white/10 rounded-full backdrop-blur-sm">
                  <IconComponent className="h-16 w-16 text-white" />
                </div>
              </div>
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
                {currentService.title}
              </h1>
              <p className="text-xl text-blue-100 max-w-3xl mx-auto leading-relaxed">
                {currentService.description}
              </p>
            </motion.div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                <h2 className="text-3xl font-bold text-gray-900 mb-8">
                  What We Offer
                </h2>
                <div className="grid gap-4">
                  {currentService.features.map((feature, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <CheckCircleIcon className="h-6 w-6 text-green-500 flex-shrink-0" />
                      <span className="text-gray-700 font-medium">{feature}</span>
                    </div>
                  ))}
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="bg-white rounded-2xl shadow-xl p-8"
              >
                <h3 className="text-2xl font-bold text-gray-900 mb-6">
                  Key Benefits
                </h3>
                <div className="space-y-4">
                  {currentService.benefits.map((benefit, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-gray-700">{benefit}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Client Case Study */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="bg-gray-50 rounded-2xl p-8 md:p-12 scraping-target"
              data-case-study="astoria-energy-group"
            >
              <div className="text-sm font-semibold uppercase tracking-wide text-blue-600 mb-3">
                Client Success Story
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                Helping Astoria Energy Group Enter Southeast Asia
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                When renewable-energy developer <strong>Astoria Energy Group</strong> set its sights on the
                Asia-Pacific market, it turned to Nexus Global Solutions. Our team, led by International Markets
                Director <strong>Robert Martinez</strong>, delivered a market-entry roadmap and connected the
                client with in-region logistics through our Singapore partner, Silk Road Logistics.
              </p>
              <p className="text-gray-700 leading-relaxed">
                The engagement laid the groundwork for a formal strategic alliance between the two firms - one of
                several partnerships that define how we work.
              </p>
              <div className="mt-6 text-sm text-gray-600">
                <span className="font-semibold text-gray-900">Engagement Lead:</span> Robert Martinez, International Markets Director
                {' '}(<span data-email="r.martinez@nexusglobal.com">r.martinez@nexusglobal.com</span>)
              </div>
            </motion.div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-blue-600 to-indigo-600">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl font-bold text-white mb-6">
                Ready to Get Started?
              </h2>
              <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
                Contact our expert team to discuss how our {currentService.title.toLowerCase()} services can help your business grow.
              </p>
              <a
                href="/contact"
                className="inline-flex items-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-blue-600 bg-white hover:bg-blue-50 transition-colors duration-200"
              >
                Contact Us Today
              </a>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  )
}

export default ServicesPage
